import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { FormikProps } from "formik";
import { Select, type SelectOption } from "@outpave/ds";
import CardPresetConfigDrawer from "components/admin/CardPresetConfigDrawer";
import Role from "enums/Role";
import { GET_CARD_PRESETS } from "graphql/cardPresets";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import { CardPolicy } from "utils/cards/cardFormUtils";
import {
  applyCardPreset,
  CardPreset,
  cardPresetFromNode,
  CardPresetNode,
  clearCardPreset,
  presetLimitsCaption,
} from "utils/cards/cardPresets";

interface CardPresetPickerProps {
  formik: FormikProps<CardPolicy>;
  /**
   * Called with the program a picked preset names, or null for a preset naming
   * none or a cleared one, which leaves the card's own.
   */
  onCardProgram?: (cardProgramId: string | null) => void;
}

/** Starts a card's policy from a saved preset. Picking none leaves the policy to the user. */
const CardPresetPicker: React.FC<CardPresetPickerProps> = ({
  formik,
  onCardProgram,
}) => {
  const { t } = useTranslation();
  const canAddPreset = useCheckUserRole(Role.Admin);
  const presetDrawer = useDrawerRecord<true>();
  // No `first`: the server then reads every page and returns all presets at once.
  const { data, loading, refetch } = useQuery(GET_CARD_PRESETS);

  const presets: CardPreset[] = useMemo(
    () =>
      (data?.cardPresets?.edges ?? []).map(
        ({ node }: { node: CardPresetNode }) => cardPresetFromNode(node),
      ),
    [data],
  );

  const { presetId, nameOfPreset } = formik.values;

  const applyPreset = (preset: CardPreset) => {
    formik.setValues(applyCardPreset(formik.values, preset));
    onCardProgram?.(preset.cardProgramId || null);
  };

  const options: SelectOption[] = useMemo(() => {
    const listed = presets.map((preset) => ({
      id: preset.id,
      label: preset.presetName,
      caption: presetLimitsCaption(preset) || undefined,
    }));
    // The DS finds a label only in `options`, so a chosen preset reads blank until the query lands.
    if (!presetId || listed.some((option) => option.id === presetId))
      return listed;
    return [{ id: presetId, label: nameOfPreset ?? "" }, ...listed];
  }, [presets, presetId, nameOfPreset]);

  if (!canAddPreset && presets.length === 0 && !presetId) return null;

  return (
    <>
      <Select
        label={t("Preset")}
        placeholder={t("Start from a preset (optional)")}
        searchable
        loading={loading}
        options={options}
        value={presetId ?? null}
        topAction={
          canAddPreset
            ? {
                label: t("Add a new preset"),
                onClick: () => presetDrawer.show(true),
              }
            : undefined
        }
        onChange={(id) => {
          if (id === presetId) return;
          const preset = presets.find((item) => item.id === id);
          if (id && !preset) return;
          if (preset) {
            applyPreset(preset);
            return;
          }
          formik.setValues(clearCardPreset(formik.values));
          onCardProgram?.(null);
        }}
        fullWidth
      />
      {presetDrawer.record && (
        <CardPresetConfigDrawer
          open={presetDrawer.open}
          handleCloseDrawer={presetDrawer.close}
          onAfterClose={presetDrawer.afterClose}
          onCreatePresetSuccess={(preset) => {
            refetch();
            applyPreset(preset);
          }}
        />
      )}
    </>
  );
};

export default CardPresetPicker;
